import { FC, useCallback, useEffect, useRef, useState } from 'react'

import { EuiButton, EuiButtonEmpty, EuiCheckbox, EuiFilePicker, EuiFlexGroup, EuiFlexItem, EuiModal, EuiModalBody, EuiModalFooter, EuiModalHeader, EuiModalHeaderTitle, EuiOverlayMask, EuiSpacer, EuiSwitch, EuiText, EuiTitle, EuiImage, EuiCallOut } from '@elastic/eui'

import adcritterLogoSvg from 'assets/AdCritter_AI_Logo_Black_Big_Squirrel.svg'
import { svgToPngDataUrl, parseSvgDimensions } from 'utils/svgToPng'

const ACCEPTED_FORMATS = '.png,.jpg,.jpeg,.webp,.svg'
const RECOMMENDED_WIDTH = 800
const RECOMMENDED_HEIGHT = 400
const ADCRITTER_SVG_WIDTH = 194
const ADCRITTER_SVG_HEIGHT = 57

export interface BrandingImage {
  dataUrl: string
  width: number
  height: number
}

export interface BrandingImages {
  agencyLogo: BrandingImage | null
  clientLogo: BrandingImage | null
  isDirect: boolean
}

const isBelowPixelationThreshold = (image: BrandingImage | null): boolean => {
  if (!image) return false
  return image.width < RECOMMENDED_WIDTH || image.height < RECOMMENDED_HEIGHT
}

interface BrandingUploadModalProps {
  initialImages: BrandingImages
  onConfirm: (images: BrandingImages) => void
  onCancel: () => void
}

const readFileAsDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

const loadImageDimensions = (dataUrl: string): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
    img.onerror = () => reject(new Error('Failed to load image'))
    img.src = dataUrl
  })
}

let cachedDefaultAgencyLogo: BrandingImage | null = null

const loadDefaultAgencyLogo = async (): Promise<BrandingImage> => {
  if (cachedDefaultAgencyLogo) return cachedDefaultAgencyLogo
  const result = await svgToPngDataUrl(adcritterLogoSvg, ADCRITTER_SVG_WIDTH, ADCRITTER_SVG_HEIGHT)
  cachedDefaultAgencyLogo = result
  return result
}

const convertToPng = (imageDataUrl: string, width: number, height: number): Promise<string> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) return reject(new Error('Canvas context unavailable'))

    const img = new Image()
    img.onload = () => {
      ctx.drawImage(img, 0, 0, width, height)
      resolve(canvas.toDataURL('image/png'))
    }
    img.onerror = () => reject(new Error('Failed to convert image'))
    img.src = imageDataUrl
  })
}

const processRasterUpload = async (file: File): Promise<BrandingImage> => {
  const dataUrl = await readFileAsDataUrl(file)
  const dimensions = await loadImageDimensions(dataUrl)

  const isWebp = file.type === 'image/webp' || file.name.toLowerCase().endsWith('.webp')
  if (isWebp) {
    const pngDataUrl = await convertToPng(dataUrl, dimensions.width, dimensions.height)
    return { dataUrl: pngDataUrl, width: dimensions.width, height: dimensions.height }
  }

  return { dataUrl, width: dimensions.width, height: dimensions.height }
}

const processSvgUpload = async (file: File): Promise<BrandingImage> => {
  const svgText = await file.text()
  const dimensions = parseSvgDimensions(svgText)

  if (!dimensions) {
    throw new Error('SVG must have a viewBox or width/height attributes.')
  }

  const dataUrl = await readFileAsDataUrl(file)
  return svgToPngDataUrl(dataUrl, dimensions.width, dimensions.height)
}

interface LogoUploadZoneProps {
  label: string
  logo: BrandingImage | null
  error: string | null
  warning: string | null
  isDefault?: boolean
  pickerRef: React.RefObject<EuiFilePicker>
  onUpload: (files: FileList | null) => void
  onRemove: () => void
}

const LogoUploadZone: FC<LogoUploadZoneProps> = ({ label, logo, error, warning, isDefault, pickerRef, onUpload, onRemove }) => (
  <EuiFlexItem>
    <EuiTitle size='xs'>
      <h4>{label}</h4>
    </EuiTitle>
    <EuiSpacer size='s' />
    {logo ? (
      <>
        <div style={{ border: '1px solid #D3DAE6', borderRadius: 4, padding: 8, textAlign: 'center', backgroundColor: '#fafbfd' }}>
          <EuiImage src={logo.dataUrl} alt={`${label} preview`} style={{ maxWidth: '100%', maxHeight: 150, objectFit: 'contain' }} />
        </div>
        <EuiSpacer size='s' />
        {isDefault ? (
          <EuiText size='xs' color='subdued'><p>Default AdCritter logo</p></EuiText>
        ) : (
          <EuiButtonEmpty size='s' color='danger' onClick={onRemove}>Remove</EuiButtonEmpty>
        )}
      </>
    ) : (
      <EuiFilePicker ref={pickerRef} accept={ACCEPTED_FORMATS} onChange={onUpload} display='large' isInvalid={!!error} />
    )}
    {error && (
      <>
        <EuiSpacer size='s' />
        <EuiCallOut color='danger' size='s' title={error} />
      </>
    )}
    {warning && !error && (
      <>
        <EuiSpacer size='s' />
        <EuiCallOut color='warning' size='s' title={warning} />
      </>
    )}
  </EuiFlexItem>
)

const BrandingUploadModal: FC<BrandingUploadModalProps> = ({ initialImages, onConfirm, onCancel }) => {
  const [agencyLogo, setAgencyLogo] = useState<BrandingImage | null>(initialImages.agencyLogo)
  const [clientLogo, setClientLogo] = useState<BrandingImage | null>(initialImages.clientLogo)
  const [isDirect, setIsDirect] = useState(initialImages.isDirect)
  const [agencyError, setAgencyError] = useState<string | null>(null)
  const [clientError, setClientError] = useState<string | null>(null)
  const [userUploadedAgencyLogo, setUserUploadedAgencyLogo] = useState<BrandingImage | null>(initialImages.agencyLogo)
  const [isDefaultAgencyLogo, setIsDefaultAgencyLogo] = useState(false)
  const [pixelationAcknowledged, setPixelationAcknowledged] = useState(false)

  const agencyPickerRef = useRef<EuiFilePicker>(null)
  const clientPickerRef = useRef<EuiFilePicker>(null)

  useEffect(() => {
    if (isDirect && !userUploadedAgencyLogo) {
      loadDefaultAgencyLogo().then(logo => {
        setAgencyLogo(logo)
        setIsDefaultAgencyLogo(true)
      })
    } else if (isDirect && userUploadedAgencyLogo) {
      setAgencyLogo(userUploadedAgencyLogo)
      setIsDefaultAgencyLogo(false)
    } else if (!isDirect) {
      setAgencyLogo(userUploadedAgencyLogo)
      setIsDefaultAgencyLogo(false)
    }
  }, [isDirect, userUploadedAgencyLogo])

  const handleFileUpload = useCallback(async (files: FileList | null, setImage: (img: BrandingImage | null) => void, setError: (err: string | null) => void) => {
    setError(null)
    if (!files || files.length === 0) return

    const file = files[0]
    try {
      const isSvg = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')
      const brandingImage = isSvg ? await processSvgUpload(file) : await processRasterUpload(file)
      setImage(brandingImage)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to read image file.')
    }
  }, [])

  const handleAgencyUpload = useCallback((files: FileList | null) => {
    handleFileUpload(files, (img) => {
      setAgencyLogo(img)
      setUserUploadedAgencyLogo(img)
      setIsDefaultAgencyLogo(false)
    }, setAgencyError)
  }, [handleFileUpload])

  const handleAgencyRemove = useCallback(() => {
    setAgencyLogo(null)
    setUserUploadedAgencyLogo(null)
    setIsDefaultAgencyLogo(false)
    setAgencyError(null)
    if (agencyPickerRef.current) agencyPickerRef.current.removeFiles()

    if (isDirect) {
      loadDefaultAgencyLogo().then(logo => {
        setAgencyLogo(logo)
        setIsDefaultAgencyLogo(true)
      })
    }
  }, [isDirect])

  const handleClientUpload = useCallback((files: FileList | null) => {
    handleFileUpload(files, setClientLogo, setClientError)
  }, [handleFileUpload])

  const handleClientRemove = useCallback(() => {
    setClientLogo(null)
    setClientError(null)
    if (clientPickerRef.current) clientPickerRef.current.removeFiles()
  }, [])

  const agencyPixelationWarning = !isDefaultAgencyLogo && isBelowPixelationThreshold(agencyLogo)
    ? `Image is ${agencyLogo!.width}x${agencyLogo!.height}px and may appear pixelated (recommended: ${RECOMMENDED_WIDTH}x${RECOMMENDED_HEIGHT}px).`
    : null
  const clientPixelationWarning = isBelowPixelationThreshold(clientLogo)
    ? `Image is ${clientLogo!.width}x${clientLogo!.height}px and may appear pixelated (recommended: ${RECOMMENDED_WIDTH}x${RECOMMENDED_HEIGHT}px).`
    : null
  const hasPixelationWarnings = !!agencyPixelationWarning || !!clientPixelationWarning

  return (
    <EuiOverlayMask>
      <EuiModal onClose={onCancel} style={{ maxWidth: 700 }}>
        <EuiModalHeader>
          <EuiModalHeaderTitle>Upload Branding</EuiModalHeaderTitle>
        </EuiModalHeader>

        <EuiModalBody>
          <EuiSwitch label='Direct client (uses "Generated by AdCritter" instead of "A Client of")' checked={isDirect} onChange={e => setIsDirect(e.target.checked)} compressed />
          <EuiSpacer size='l' />
          <EuiFlexGroup gutterSize='l'>
            <LogoUploadZone
              label='Agency Logo'
              logo={agencyLogo}
              error={agencyError}
              warning={agencyPixelationWarning}
              isDefault={isDefaultAgencyLogo}
              pickerRef={agencyPickerRef}
              onUpload={handleAgencyUpload}
              onRemove={handleAgencyRemove}
            />
            <LogoUploadZone
              label='Client Logo'
              logo={clientLogo}
              error={clientError}
              warning={clientPixelationWarning}
              pickerRef={clientPickerRef}
              onUpload={handleClientUpload}
              onRemove={handleClientRemove}
            />
          </EuiFlexGroup>

          {hasPixelationWarnings && (
            <>
              <EuiSpacer size='m' />
              <EuiCheckbox
                id='pixelation-acknowledgment'
                label='I acknowledge that the uploaded image(s) may appear pixelated in the PDF output and I will verify the result'
                checked={pixelationAcknowledged}
                onChange={e => setPixelationAcknowledged(e.target.checked)}
              />
            </>
          )}

          <EuiSpacer size='m' />
          <EuiText size='xs' color='subdued'>
            <p>Accepted formats: PNG, JPG, WEBP, SVG. Recommended: {RECOMMENDED_WIDTH}x{RECOMMENDED_HEIGHT}px or larger for best print quality (not applicable to SVG).</p>
          </EuiText>
        </EuiModalBody>

        <EuiModalFooter>
          <EuiButtonEmpty onClick={onCancel}>Cancel</EuiButtonEmpty>
          <EuiButtonEmpty onClick={() => onConfirm({ agencyLogo: null, clientLogo: null, isDirect })}>Generate Without Logos</EuiButtonEmpty>
          <EuiButton onClick={() => onConfirm({ agencyLogo, clientLogo, isDirect })} fill disabled={!!agencyError || !!clientError || (hasPixelationWarnings && !pixelationAcknowledged)}>
            Generate
          </EuiButton>
        </EuiModalFooter>
      </EuiModal>
    </EuiOverlayMask>
  )
}

export default BrandingUploadModal
